/**
 * Problem generators for the four arithmetic worksheets.
 *
 * Every generator is a pure function of its options and an `Rng`, and every constraint a
 * teacher can set ("no regrouping", "no remainders", "borrow across zeros") is either
 * built in by construction or checked after the fact. Constraints that cannot always be
 * met — a one-digit sum that must regroup yet stay within 10 — report `unmet` instead of
 * silently handing back problems that break the setting.
 */

import {
  pick,
  type Rng,
  randInt,
  shuffled,
} from "@/lib/tool-kit/worksheets/random";

export type Operation = "add" | "subtract" | "multiply" | "divide";
export type Blank = "answer" | "first" | "second";
export type RegroupMode = "any" | "none" | "always";
export type BorrowMode = "any" | "none" | "always" | "zeros";
export type RemainderMode = "none" | "any" | "always";

export const OPERATION_SYMBOL: Record<Operation, string> = {
  add: "+",
  subtract: "−",
  multiply: "×",
  divide: "÷",
};

export interface ArithmeticProblem {
  op: Operation;
  /** Addends, or `[minuend, subtrahend]`, `[factor, factor]`, `[dividend, divisor]`. */
  terms: number[];
  /** The sum, difference, product or quotient (whole part). */
  answer: number;
  /** Division only; 0 otherwise. */
  remainder: number;
  /** Which part a one-line equation leaves empty. */
  blank: Blank;
}

export interface Generated {
  problems: ArithmeticProblem[];
  /** Problems that could not satisfy every setting (the ranges contradict each other). */
  unmet: number;
}

export interface Candidate<T> {
  item: T;
  ok: boolean;
}

const SLOT_ATTEMPTS = 120;
const MAX_DIGITS = 6;

export function clampDigits(value: number, min = 1, max = MAX_DIGITS): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Smallest and largest whole number with exactly `digits` digits (`0` allowed for one). */
export function digitRange(
  digits: number,
  allowZero = false
): [number, number] {
  if (digits <= 1) {
    return [allowZero ? 0 : 1, 9];
  }
  return [10 ** (digits - 1), 10 ** digits - 1];
}

/** Base-10 digits, least significant first. */
export function digitsLsb(value: number): number[] {
  const digits: number[] = [];
  let rest = Math.abs(Math.trunc(value));
  do {
    digits.push(rest % 10);
    rest = Math.trunc(rest / 10);
  } while (rest > 0);
  return digits;
}

function fromDigitsLsb(digits: readonly number[]): number {
  let value = 0;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    value = value * 10 + (digits[i] ?? 0);
  }
  return value;
}

/** How many columns carry when the terms are added the school way. */
export function countCarries(terms: readonly number[]): number {
  const columns = terms.map(digitsLsb);
  const width = Math.max(...columns.map((c) => c.length));
  let carry = 0;
  let carries = 0;
  for (let i = 0; i < width; i += 1) {
    const sum = columns.reduce((total, c) => total + (c[i] ?? 0), carry);
    carry = Math.trunc(sum / 10);
    if (carry > 0) {
      carries += 1;
    }
  }
  return carries;
}

export interface BorrowInfo {
  borrows: number;
  /** A borrow had to pass through a zero in the minuend. */
  acrossZero: boolean;
}

/** Borrowing in `minuend − subtrahend` (both non-negative, minuend not smaller). */
export function borrowInfo(minuend: number, subtrahend: number): BorrowInfo {
  const top = digitsLsb(minuend);
  const bottom = digitsLsb(subtrahend);
  let borrowIn = 0;
  let borrows = 0;
  let acrossZero = false;
  for (let i = 0; i < top.length; i += 1) {
    const topDigit = top[i] ?? 0;
    if (borrowIn === 1 && topDigit === 0) {
      acrossZero = true;
    }
    const diff = topDigit - borrowIn - (bottom[i] ?? 0);
    borrowIn = diff < 0 ? 1 : 0;
    borrows += borrowIn;
  }
  return { borrows, acrossZero };
}

/**
 * Fills `count` slots, preferring problems that meet every setting and have not
 * appeared yet. A slot falls back to a repeat when the pool is small, and to an unmet
 * problem (counted) when the settings contradict each other.
 */
export function collect<T>(
  count: number,
  rng: Rng,
  make: (rng: Rng) => Candidate<T>,
  keyOf: (item: T) => string
): { items: T[]; unmet: number } {
  const seen = new Set<string>();
  const items: T[] = [];
  let satisfied: T | null = null;
  let unmet = 0;
  for (let slot = 0; slot < count; slot += 1) {
    let fresh: T | null = null;
    let repeat: T | null = null;
    let broken: T | null = null;
    for (
      let attempt = 0;
      attempt < SLOT_ATTEMPTS && fresh === null;
      attempt += 1
    ) {
      const candidate = make(rng);
      if (!candidate.ok) {
        broken = broken ?? candidate.item;
      } else if (seen.has(keyOf(candidate.item))) {
        repeat = repeat ?? candidate.item;
      } else {
        fresh = candidate.item;
      }
    }
    // A tiny pool of valid problems can be missed by chance; reuse a valid one rather
    // than report a rule as broken.
    const chosen: T | null = fresh ?? repeat ?? satisfied;
    if (chosen !== null) {
      items.push(chosen);
      satisfied = chosen;
      seen.add(keyOf(chosen));
    } else if (broken !== null) {
      items.push(broken);
      unmet += 1;
    }
  }
  return { items, unmet };
}

const keyOfProblem = (p: ArithmeticProblem) =>
  `${p.op}:${p.terms.join(",")}:${p.blank}`;

function problem(
  op: Operation,
  terms: number[],
  answer: number,
  remainder = 0,
  blank: Blank = "answer"
): ArithmeticProblem {
  return { op, terms, answer, remainder, blank };
}

/* -------------------------------------------------------------------- addition */

export interface AdditionOptions {
  count: number;
  /** Number of addends, 2 to 4. */
  addends: number;
  /** Digits in the first addend, then in every other addend. */
  firstDigits: number;
  otherDigits: number;
  regroup: RegroupMode;
  /** Largest allowed sum; 0 for no limit. */
  maxSum: number;
  allowZero: boolean;
  /** Leave a term blank instead of the sum (two addends only). */
  missing: MissingMode;
}

/**
 * Digits for addends so that no column adds past 9. Built column by column from the
 * right: each column shares a budget of 9 among the addends that reach it, keeping the
 * leading digit of every addend non-zero (except a lone 0 when zero is allowed).
 */
function noCarryAddends(
  lengths: readonly number[],
  allowZero: boolean,
  rng: Rng
): number[] {
  const columns = lengths.map((length) => new Array<number>(length).fill(0));
  const width = Math.max(...lengths);
  for (let c = 0; c < width; c += 1) {
    const active = shuffled(
      rng,
      lengths
        .map((length, index) => ({ length, index }))
        .filter((e) => e.length > c)
    );
    const floorOf = (length: number) =>
      c === length - 1 && !(length === 1 && allowZero) ? 1 : 0;
    let floorsAhead = active.reduce((sum, e) => sum + floorOf(e.length), 0);
    let used = 0;
    for (const entry of active) {
      const floor = floorOf(entry.length);
      floorsAhead -= floor;
      const digit = randInt(
        rng,
        floor,
        Math.max(floor, 9 - used - floorsAhead)
      );
      used += digit;
      const target = columns[entry.index];
      if (target) {
        target[c] = digit;
      }
    }
  }
  return columns.map(fromDigitsLsb);
}

function randomWithDigits(
  rng: Rng,
  digits: number,
  allowZero: boolean,
  cap = 0
): number {
  const [low, high] = digitRange(digits, allowZero);
  return randInt(rng, low, cap > 0 ? Math.max(low, Math.min(high, cap)) : high);
}

function additionCandidate(
  options: AdditionOptions,
  rng: Rng
): Candidate<ArithmeticProblem> {
  const lengths = Array.from({ length: options.addends }, (_, i) =>
    i === 0 ? options.firstDigits : options.otherDigits
  );
  const lows = lengths.map(
    (length) => digitRange(length, options.allowZero)[0]
  );
  const lowTotal = lows.reduce((a, b) => a + b, 0);
  const terms =
    options.regroup === "none"
      ? noCarryAddends(lengths, options.allowZero, rng)
      : lengths.map((length, i) =>
          randomWithDigits(
            rng,
            length,
            options.allowZero,
            options.maxSum > 0
              ? options.maxSum - (lowTotal - (lows[i] ?? 0))
              : 0
          )
        );
  const sum = terms.reduce((a, b) => a + b, 0);
  const withinMax = options.maxSum <= 0 || sum <= options.maxSum;
  const regroupOk = options.regroup !== "always" || countCarries(terms) > 0;
  const blank = blankFor(options.missing, rng, options.addends === 2);
  return {
    item: problem("add", terms, sum, 0, blank),
    ok: withinMax && regroupOk,
  };
}

export function generateAddition(
  options: AdditionOptions,
  rng: Rng
): Generated {
  const { items, unmet } = collect(
    options.count,
    rng,
    (r) => additionCandidate(options, r),
    keyOfProblem
  );
  return { problems: items, unmet };
}

/* ----------------------------------------------------------------- subtraction */

export interface SubtractionOptions {
  count: number;
  minuendDigits: number;
  subtrahendDigits: number;
  borrow: BorrowMode;
  /** Let the difference go below zero (the subtrahend may then be the larger number). */
  allowNegative: boolean;
  /** Largest minuend; 0 for no limit. */
  maxMinuend: number;
  allowZero: boolean;
  /** Leave a term blank instead of the difference. */
  missing: MissingMode;
}

/** Minuend and subtrahend with no column needing a borrow. */
function noBorrowPair(
  minuendDigits: number,
  subtrahendDigits: number,
  allowZero: boolean,
  rng: Rng
): [number, number] {
  const width = Math.max(minuendDigits, subtrahendDigits);
  const top: number[] = [];
  const bottom: number[] = [];
  for (let c = 0; c < width; c += 1) {
    const topLead = c === minuendDigits - 1;
    const bottomLead = c === subtrahendDigits - 1;
    const topFloor = topLead && !(minuendDigits === 1 && allowZero) ? 1 : 0;
    const bottomFloor =
      bottomLead && !(subtrahendDigits === 1 && allowZero) ? 1 : 0;
    const hasBottom = c < subtrahendDigits;
    const hasTop = c < minuendDigits;
    const b = hasBottom ? randInt(rng, bottomFloor, 9) : 0;
    const t = hasTop ? randInt(rng, Math.max(topFloor, b), 9) : 0;
    top.push(t);
    bottom.push(b);
  }
  return [fromDigitsLsb(top), fromDigitsLsb(bottom)];
}

/** A minuend with zeros inside it (not the leading digit), for borrowing across zeros. */
function zeroRichMinuend(digits: number, rng: Rng): number {
  const places: number[] = [];
  for (let c = digits - 1; c >= 0; c -= 1) {
    if (c === digits - 1) {
      places.push(randInt(rng, 1, 9));
    } else if (c === 0) {
      places.push(randInt(rng, 0, 9));
    } else {
      places.push(rng() < 0.7 ? 0 : randInt(rng, 1, 9));
    }
  }
  return Number(places.join(""));
}

function borrowOk(
  mode: BorrowMode,
  minuend: number,
  subtrahend: number
): boolean {
  if (mode === "any") {
    return true;
  }
  const info = borrowInfo(minuend, subtrahend);
  switch (mode) {
    case "none":
      return info.borrows === 0;
    case "always":
      return info.borrows > 0;
    default:
      return info.acrossZero;
  }
}

function subtractionPair(
  options: SubtractionOptions,
  rng: Rng
): [number, number] {
  const { minuendDigits, subtrahendDigits, allowZero } = options;
  if (options.borrow === "none" && !options.allowNegative) {
    return noBorrowPair(minuendDigits, subtrahendDigits, allowZero, rng);
  }
  if (options.borrow === "zeros" && minuendDigits >= 3) {
    return [
      zeroRichMinuend(minuendDigits, rng),
      randomWithDigits(rng, subtrahendDigits, allowZero),
    ];
  }
  return [
    randomWithDigits(rng, minuendDigits, allowZero, options.maxMinuend),
    randomWithDigits(rng, subtrahendDigits, allowZero),
  ];
}

function subtractionCandidate(
  options: SubtractionOptions,
  rng: Rng
): Candidate<ArithmeticProblem> {
  let [minuend, subtrahend] = subtractionPair(options, rng);
  if (!options.allowNegative && subtrahend > minuend) {
    [minuend, subtrahend] = [subtrahend, minuend];
  }
  const withinMax = options.maxMinuend <= 0 || minuend <= options.maxMinuend;
  // Borrow rules describe the positive subtraction; a negative answer is its own lesson.
  const borrow =
    options.borrow === "zeros" && options.minuendDigits < 3
      ? "always"
      : options.borrow;
  const borrowFits =
    subtrahend > minuend || borrowOk(borrow, minuend, subtrahend);
  const blank = blankFor(options.missing, rng, true);
  return {
    item: problem(
      "subtract",
      [minuend, subtrahend],
      minuend - subtrahend,
      0,
      blank
    ),
    ok: withinMax && borrowFits,
  };
}

export function generateSubtraction(
  raw: SubtractionOptions,
  rng: Rng
): Generated {
  // Without negative answers the subtrahend cannot be longer than the minuend.
  const options = raw.allowNegative
    ? raw
    : {
        ...raw,
        subtrahendDigits: Math.min(raw.subtrahendDigits, raw.minuendDigits),
      };
  const { items, unmet } = collect(
    options.count,
    rng,
    (r) => subtractionCandidate(options, r),
    keyOfProblem
  );
  return { problems: items, unmet };
}

/* -------------------------------------------------------------- multiplication */

export type MissingMode = "answer" | "missing" | "mixed";

export interface TablesMultiplicationOptions {
  kind: "tables";
  count: number;
  /** Times tables to practise, each 0 to 12. */
  tables: readonly number[];
  /** The other factor runs from `otherMin` to `otherMax`. */
  otherMin: number;
  otherMax: number;
  /** Put the table number second as often as first. */
  mixOrder: boolean;
  missing: MissingMode;
}

export interface DigitsMultiplicationOptions {
  kind: "digits";
  count: number;
  firstDigits: number;
  secondDigits: number;
  /** No digit of either factor is 0. */
  noZeroDigits: boolean;
}

export type MultiplicationOptions =
  | TablesMultiplicationOptions
  | DigitsMultiplicationOptions;

function blankFor(mode: MissingMode, rng: Rng, canBlankTerm: boolean): Blank {
  if (mode === "answer" || !canBlankTerm) {
    return "answer";
  }
  if (mode === "missing") {
    return pick(rng, ["first", "second"] as const);
  }
  return pick(rng, ["answer", "answer", "first", "second"] as const);
}

function tablesCandidate(
  options: TablesMultiplicationOptions,
  rng: Rng
): Candidate<ArithmeticProblem> {
  const table = options.tables.length > 0 ? pick(rng, options.tables) : 1;
  const other = randInt(rng, options.otherMin, options.otherMax);
  const flip = options.mixOrder && rng() < 0.5;
  const terms = flip ? [other, table] : [table, other];
  const [a, b] = terms as [number, number];
  return {
    item: problem(
      "multiply",
      terms,
      a * b,
      0,
      blankFor(options.missing, rng, a !== 0 && b !== 0)
    ),
    ok: options.tables.length > 0,
  };
}

function hasZeroDigit(value: number): boolean {
  return digitsLsb(value).includes(0);
}

function digitsCandidate(
  options: DigitsMultiplicationOptions,
  rng: Rng
): Candidate<ArithmeticProblem> {
  const a = randomWithDigits(rng, options.firstDigits, false);
  const b = randomWithDigits(rng, options.secondDigits, false);
  const clean = !(options.noZeroDigits && (hasZeroDigit(a) || hasZeroDigit(b)));
  return { item: problem("multiply", [a, b], a * b), ok: clean };
}

export function generateMultiplication(
  options: MultiplicationOptions,
  rng: Rng
): Generated {
  const { items, unmet } = collect(
    options.count,
    rng,
    options.kind === "tables"
      ? (r) => tablesCandidate(options, r)
      : (r) => digitsCandidate(options, r),
    keyOfProblem
  );
  return { problems: items, unmet };
}

/* -------------------------------------------------------------------- division */

export interface FactsDivisionOptions {
  kind: "facts";
  count: number;
  divisorMin: number;
  divisorMax: number;
  /** Quotients run from 1 to this. */
  quotientMax: number;
  remainder: RemainderMode;
}

export interface DigitsDivisionOptions {
  kind: "digits";
  count: number;
  dividendDigits: number;
  /** 1 gives divisors 2–9; 2 gives 10–99. */
  divisorDigits: number;
  remainder: RemainderMode;
}

export type DivisionOptions = FactsDivisionOptions | DigitsDivisionOptions;

function remainderFor(mode: RemainderMode, divisor: number, rng: Rng): number {
  if (mode === "none" || divisor < 2) {
    return 0;
  }
  return randInt(rng, mode === "always" ? 1 : 0, divisor - 1);
}

function divisionProblem(
  divisor: number,
  quotient: number,
  remainder: number
): ArithmeticProblem {
  return problem(
    "divide",
    [divisor * quotient + remainder, divisor],
    quotient,
    remainder
  );
}

function factsDivisionCandidate(
  options: FactsDivisionOptions,
  rng: Rng
): Candidate<ArithmeticProblem> {
  const divisor = randInt(rng, options.divisorMin, options.divisorMax);
  const quotient = randInt(rng, 1, options.quotientMax);
  const remainder = remainderFor(options.remainder, divisor, rng);
  const ok = options.remainder !== "always" || remainder > 0;
  return { item: divisionProblem(divisor, quotient, remainder), ok };
}

function digitsDivisionCandidate(
  options: DigitsDivisionOptions,
  rng: Rng
): Candidate<ArithmeticProblem> {
  const divisor =
    options.divisorDigits <= 1 ? randInt(rng, 2, 9) : randInt(rng, 10, 99);
  const remainder = remainderFor(options.remainder, divisor, rng);
  const [low, high] = digitRange(options.dividendDigits);
  const minQuotient = Math.max(1, Math.ceil((low - remainder) / divisor));
  const maxQuotient = Math.floor((high - remainder) / divisor);
  const feasible = minQuotient <= maxQuotient;
  const quotient = feasible ? randInt(rng, minQuotient, maxQuotient) : 1;
  const ok = feasible && (options.remainder !== "always" || remainder > 0);
  return { item: divisionProblem(divisor, quotient, remainder), ok };
}

export function generateDivision(
  options: DivisionOptions,
  rng: Rng
): Generated {
  const { items, unmet } = collect(
    options.count,
    rng,
    options.kind === "facts"
      ? (r) => factsDivisionCandidate(options, r)
      : (r) => digitsDivisionCandidate(options, r),
    keyOfProblem
  );
  return { problems: items, unmet };
}

/* --------------------------------------------------------------------- display */

/** A number with a true minus sign for negatives. */
export function formatNumber(value: number): string {
  return value < 0 ? `−${Math.abs(value)}` : String(value);
}

/** The answer as printed in a key: `42`, or `12 R 3` with the remainder abbreviation. */
export function formatAnswer(
  p: ArithmeticProblem,
  remainderLabel: string
): string {
  if (p.op === "divide" && p.remainder > 0) {
    return `${formatNumber(p.answer)} ${remainderLabel} ${p.remainder}`;
  }
  return formatNumber(p.answer);
}

/**
 * The widest problem in characters, for choosing a column count. Stacked problems are as
 * wide as their longest term plus the operator; one-line problems carry the whole
 * equation and an answer blank.
 */
export function problemWidth(
  problems: readonly ArithmeticProblem[],
  layout: "vertical" | "horizontal"
): number {
  let widest = 0;
  for (const p of problems) {
    const longest = Math.max(...p.terms.map((t) => String(t).length));
    let width: number;
    if (layout === "horizontal") {
      width = p.terms.map(String).join(" × ").length + 3 + 4;
    } else if (p.op === "divide") {
      width =
        p.terms.map((t) => String(t).length).reduce((a, b) => a + b, 0) + 3;
    } else {
      width = Math.max(longest + 2, String(p.answer).length);
    }
    widest = Math.max(widest, width);
  }
  return widest;
}
